import { LDateTime } from '@app/components/date/LDateTime';
import { UserLabel } from '@app/components/users/UserLabel';
import { PlaybackGuard } from '@app/core/playback/context/PlaybackGuard';
import { FragmentDuration } from '@app/modules/fragments/components/type/FragmentDuration';
import { DateTimeStr } from '@mi-front/core';
import { LocaleRelativeTime } from '@mi-front/ui';
import { Box, Card, CardContent, Stack } from '@mui/material';
import { useFragment } from '../../hooks/useFragment';
import { FragmentGoToStreamFromPlaybackAction } from './actions/FragmentGoToStreamFromPlaybackAction';
import { FragmentGoToStreamFromStartAction } from './actions/FragmentGoToStreamFromStartAction';
import { FragmentMetadataTags } from './FragmentTags';

/**
 * Displays fragment metadata (date, duration, creator, description, tags) in the fragment view.
 */
export function FragmentMetadata() {
  const { fragment, station } = useFragment();
  const description = fragment.description?.trim();

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1}
            alignItems="center"
            sx={{ typography: 'body2', color: 'text.secondary' }}
          >
            <Stack direction="row" alignItems="center" gap={0.5}>
              <LDateTime value={fragment.from as DateTimeStr} showRelative="inline" />
              <PlaybackGuard>
                <FragmentGoToStreamFromStartAction
                  stationSlug={station.slug}
                  fragmentFrom={fragment.from as DateTimeStr}
                />
                <FragmentGoToStreamFromPlaybackAction stationSlug={station.slug} />
              </PlaybackGuard>
            </Stack>
            <Box component="span">•</Box>
            <FragmentDuration fragment={fragment} />
            {!!fragment.tags?.length && (
              <>
                <Box component="span">•</Box>
                <FragmentMetadataTags stationSlug={station.slug} tags={fragment.tags} />
              </>
            )}
          </Stack>
          <Box>
            {fragment.creator ? (
              <Box
                component="span"
                sx={{
                  ml: 'auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  columnGap: 1,
                  typography: 'body2',
                  color: 'text.secondary',
                  flexShrink: 0,
                }}
              >
                <UserLabel
                  showIcon
                  id={fragment.creator.id}
                  user={{
                    name: fragment.creator.name,
                    surname: fragment.creator.surname,
                    email: fragment.creator.email,
                  }}
                />
                <Box component="span">•</Box>
                <LocaleRelativeTime value={fragment.createdAt} />
              </Box>
            ) : null}
          </Box>
        </Box>
        {description && (
          <Box sx={{ mt: 1.5, typography: 'body1', color: 'text.primary', whiteSpace: 'pre-wrap' }}>
            {description}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
